import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, CheckCircle2Icon, GlobeIcon, NewspaperIcon, LinkedinIcon, Loader2Icon, MapPinIcon, SearchIcon, SendIcon, ShieldCheckIcon, SparklesIcon, XIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { NETWORKING_GUESTS, NetworkingGuest } from '../data/networking-guests';

/**
 * Google Apps Script web-app endpoint that appends each introduction request
 * to the Association's spreadsheet. Replace with the deployed /exec URL.
 * Until then, requests fall back to an email to CONTACT_EMAIL.
 */
const INTRO_SHEET_URL = 'https://script.google.com/macros/s/AKfycbzG_BiMq_VHKVkDH_U1JFkDi9aw84wL5nB-tFlH2Gvs8fZpo5ta1nypS4JZf8XsCYHNhA/exec';
const CONTACT_EMAIL = 'info@honduranamerican.org';
const GOLD = '#c9a227';
const NAVY = '#0b1f3f';
const COPY = {
  EN: {
    back: 'Back to event',
    eyebrow: 'Voices of Impact · New York City',
    title: 'Guest Networking Directory',
    intro: 'Everyone in this directory attended Voices of Impact and opted in to keep the conversation going. Browse the room, then request an introduction — the Association passes your note along, so no one’s inbox is published here.',
    privacy: 'No email addresses are shown. Requests are reviewed by the Association before an introduction is made.',
    searchLabel: 'Search guests',
    searchPlaceholder: 'Search by name, role, or organization',
    all: 'All',
    countOne: 'guest',
    countMany: 'guests',
    empty: 'No guests match your search.',
    clear: 'Clear search',
    lookingFor: 'Open to',
    boardBadge: 'HAA Board',
    linkedin: 'LinkedIn',
    website: 'Website',
    feature: 'Feature',
    requestIntro: 'Request an introduction',
    introTo: 'Request an introduction to',
    formIntro: 'Tell us a little about yourself and why you’d like to connect. We’ll forward your note and let you know when it’s been passed along.',
    yourName: 'Your name',
    yourEmail: 'Your email',
    yourOrg: 'Organization or company',
    yourRole: 'Your role or title',
    yourLink: 'LinkedIn or website',
    yourLinkPlaceholder: 'linkedin.com/in/yourname',
    yourLinkHelp: 'Optional, but it helps the guest know who they are meeting.',
    message: 'Why would you like to connect?',
    messagePlaceholder: 'A sentence or two about what you’d like to discuss or explore together.',
    required: 'Required',
    cancel: 'Cancel',
    send: 'Send request',
    sending: 'Sending…',
    successTitle: 'Request sent',
    successText: 'Thank you. The Association has your request and will pass it along to',
    done: 'Close',
    fallbackNote: 'Your email app will open with the request ready to send.',
    footer: 'Hosted by the Honduran-American Association'
  },
  ES: {
    back: 'Volver al evento',
    eyebrow: 'Voices of Impact · Ciudad de Nueva York',
    title: 'Directorio de Networking',
    intro: 'Todas las personas en este directorio asistieron a Voices of Impact y decidieron seguir la conversación. Explore la sala y solicite una presentación: la Asociación hace llegar su mensaje, por lo que aquí no se publica ningún correo.',
    privacy: 'No se muestran correos electrónicos. La Asociación revisa cada solicitud antes de hacer una presentación.',
    searchLabel: 'Buscar invitados',
    searchPlaceholder: 'Buscar por nombre, cargo u organización',
    all: 'Todos',
    countOne: 'invitado',
    countMany: 'invitados',
    empty: 'Ningún invitado coincide con su búsqueda.',
    clear: 'Limpiar búsqueda',
    lookingFor: 'Abierto a',
    boardBadge: 'Junta HAA',
    linkedin: 'LinkedIn',
    website: 'Sitio web',
    feature: 'Reportaje',
    requestIntro: 'Solicitar una presentación',
    introTo: 'Solicitar una presentación con',
    formIntro: 'Cuéntenos brevemente sobre usted y por qué desea conectar. Haremos llegar su mensaje y le avisaremos cuando se haya enviado.',
    yourName: 'Su nombre',
    yourEmail: 'Su correo electrónico',
    yourOrg: 'Organización o empresa',
    yourRole: 'Su cargo o título',
    yourLink: 'LinkedIn o sitio web',
    yourLinkPlaceholder: 'linkedin.com/in/sunombre',
    yourLinkHelp: 'Opcional, pero ayuda a que el invitado sepa a quién va a conocer.',
    message: '¿Por qué le gustaría conectar?',
    messagePlaceholder: 'Una o dos frases sobre lo que le gustaría conversar o explorar juntos.',
    required: 'Obligatorio',
    cancel: 'Cancelar',
    send: 'Enviar solicitud',
    sending: 'Enviando…',
    successTitle: 'Solicitud enviada',
    successText: 'Gracias. La Asociación recibió su solicitud y la hará llegar a',
    done: 'Cerrar',
    fallbackNote: 'Se abrirá su aplicación de correo con la solicitud lista para enviar.',
    footer: 'Organizado por la Asociación Hondureño-Americana'
  }
};
function initialsOf(name: string) {
  return name.split(' ').filter(Boolean).slice(0, 2).map(part => part[0]).join('').toUpperCase();
}
type FormState = {
  name: string;
  email: string;
  org: string;
  role: string;
  link: string;
  message: string;
};
const EMPTY_FORM: FormState = {
  name: '',
  email: '',
  org: '',
  role: '',
  link: '',
  message: ''
};
function IntroRequestDialog({
  guest,
  onClose,
  c
}: {
  guest: NetworkingGuest;
  onClose: () => void;
  c: (typeof COPY)['EN'];
}) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent'>('idle');
  const isValid = form.name.trim() !== '' && /^\S+@\S+\.\S+$/.test(form.email.trim()) && form.message.trim() !== '';
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const submit = () => {
    if (!isValid || status !== 'idle') return;
    setStatus('sending');
    const payload = {
      type: 'voices-ny-introduction',
      timestamp: new Date().toISOString(),
      requestedGuestId: guest.id,
      requestedGuestName: guest.name,
      requesterName: form.name.trim(),
      requesterEmail: form.email.trim(),
      requesterOrg: form.org.trim(),
      requesterRole: form.role.trim(),
      requesterLink: form.link.trim(),
      message: form.message.trim()
    };
    if (INTRO_SHEET_URL) {
      // text/plain avoids a CORS preflight; the JSON arrives in
      // e.postData.contents on the Apps Script side.
      fetch(INTRO_SHEET_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'text/plain;charset=utf-8'
        },
        body: JSON.stringify(payload)
      }).catch(err => {
        console.warn('Introduction request submission error:', err);
      });
    } else {
      const subject = `Introduction request — ${guest.name}`;
      const body = [`Requested guest: ${guest.name}`, `From: ${form.name.trim()}`, `Email: ${form.email.trim()}`, form.org.trim() ? `Organization / company: ${form.org.trim()}` : '', form.role.trim() ? `Role / title: ${form.role.trim()}` : '', form.link.trim() ? `LinkedIn / website: ${form.link.trim()}` : '', '', form.message.trim()].filter(Boolean).join('\n');
      window.location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }
    window.setTimeout(() => setStatus('sent'), 600);
  };
  const field = 'w-full rounded-xl bg-white/5 border border-white/15 px-4 py-3 text-sm text-white placeholder-white/35 outline-none transition-colors focus:border-[#c9a227] focus:bg-white/10';
  const label = 'block text-[11px] font-bold uppercase tracking-[0.18em] text-white/55 mb-2';
  return <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 sm:p-6 overflow-y-auto" role="dialog" aria-modal="true" aria-label={`${c.introTo} ${guest.name}`} data-id="element-4089" data-mp="w1dlqwf71645">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} data-id="element-4090" data-mp="2ou0h2iu9c3rz" />
      <motion.div initial={{
      opacity: 0,
      scale: 0.96,
      y: 12
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.97,
      y: 8
    }} transition={{
      duration: 0.24,
      ease: [0.23, 1, 0.32, 1]
    }} className="relative w-full max-w-lg my-auto rounded-2xl border border-[#c9a227]/30 bg-[#0b1f3f] shadow-2xl" data-id="element-4091" data-mp="215hcadaf294n">
        <button onClick={onClose} aria-label={c.cancel} className="absolute right-4 top-4 w-9 h-9 rounded-full flex items-center justify-center text-white/60 transition-colors hover:bg-white/10 hover:text-white" data-id="element-4092" data-mp="1i7o877o23t5o">
          <XIcon className="w-5 h-5" data-id="element-4093" data-mp="13n2aso88ezkd" />
        </button>

        {status === 'sent' ? <div className="px-8 py-14 text-center" data-id="element-4094" data-mp="yo7hutgjvumj">
            <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-[#c9a227]/15 flex items-center justify-center" data-id="element-4095" data-mp="1cm5spovnx8xa">
              <CheckCircle2Icon className="w-7 h-7" style={{
            color: GOLD
          }} data-id="element-4096" data-mp="17m1bfwmxka60" />
            </div>
            <h2 className="font-bebas text-3xl tracking-wider text-white mb-3" data-id="element-4097" data-mp="1mj1uzcuywyte">
              {c.successTitle}
            </h2>
            <p className="text-sm leading-relaxed text-white/70 max-w-sm mx-auto" data-id="element-4098" data-mp="1hkqlj38l4qbp">
              {c.successText}{' '}
              <span className="font-semibold text-white" data-id="element-4099" data-mp="1ve81yf5z5mxi">{guest.name}</span>.
            </p>
            <button onClick={onClose} className="mt-8 inline-flex items-center justify-center px-7 py-3 rounded-full text-sm font-bold text-[#0b1f3f] transition-transform duration-150 hover:scale-[1.02]" style={{
          backgroundColor: GOLD
        }} data-id="element-4100" data-mp="1rcwjctliq1yt">
              {c.done}
            </button>
          </div> : <div className="px-6 sm:px-8 pt-8 pb-7" data-id="element-4101" data-mp="3n00iyr2ta5u">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] mb-2" style={{
          color: GOLD
        }} data-id="element-4102" data-mp="3vrcai7dg4l7e">
              {c.introTo}
            </p>
            <h2 className="font-bebas text-3xl sm:text-4xl tracking-wider text-white leading-none mb-1" data-id="element-4103" data-mp="rddsyqdkpb0y">
              {guest.name}
            </h2>
            <p className="text-sm text-white/55 mb-5" data-id="element-4104" data-mp="nd3o5h1ioqno">
              {guest.title ?? guest.organization ?? guest.sector}
            </p>
            <p className="text-sm leading-relaxed text-white/70 mb-7" data-id="element-4105" data-mp="iesb6xfxe018">
              {c.formIntro}
            </p>

            <div className="space-y-4" data-id="element-4106" data-mp="332wy7slp8gzr">
              <div data-id="element-4107" data-mp="333yce5y0hjlp">
                <label className={label} htmlFor="intro-name" data-id="element-4108" data-mp="33480tp3eripa">
                  {c.yourName} <span style={{
                color: GOLD
              }} data-id="element-4109" data-mp="2y2si43opdoa2">*</span>
                </label>
                <input id="intro-name" type="text" value={form.name} onChange={e => setForm({
              ...form,
              name: e.target.value
            })} className={field} autoComplete="name" data-id="element-4110" data-mp="2o0exc8r8gqhu" />
              </div>
              <div data-id="element-4111" data-mp="32puo3oqrkvxj">
                <label className={label} htmlFor="intro-email" data-id="element-4112" data-mp="32qcg296e8nzn">
                  {c.yourEmail} <span style={{
                color: GOLD
              }} data-id="element-4113" data-mp="36pcyhqkylom4">*</span>
                </label>
                <input id="intro-email" type="email" value={form.email} onChange={e => setForm({
              ...form,
              email: e.target.value
            })} className={field} autoComplete="email" data-id="element-4114" data-mp="3boy76spxlbc1" />
              </div>
              <div data-id="element-4115" data-mp="1mbl1az267f8t">
                <label className={label} htmlFor="intro-org" data-id="element-4116" data-mp="1mc48plu72l8l">
                  {c.yourOrg}
                </label>
                <input id="intro-org" type="text" value={form.org} onChange={e => setForm({
              ...form,
              org: e.target.value
            })} className={field} autoComplete="organization" data-id="element-4117" data-mp="1rbqkrryadvft" />
              </div>
              <div data-id="element-4118" data-mp="1o8187xqgued5">
                <label className={label} htmlFor="intro-role" data-id="element-4119" data-mp="1o7i2dfe3bgfh">
                  {c.yourRole}
                </label>
                <input id="intro-role" type="text" value={form.role} onChange={e => setForm({
              ...form,
              role: e.target.value
            })} className={field} autoComplete="organization-title" data-id="element-4120" data-mp="226r5peh76g21" />
              </div>
              <div data-id="element-4121" data-mp="26zaul7lc4xm5">
                <label className={label} htmlFor="intro-link" data-id="element-4122" data-mp="270vdu1278u4p">
                  {c.yourLink}
                </label>
                <input id="intro-link" type="url" inputMode="url" value={form.link} onChange={e => setForm({
              ...form,
              link: e.target.value
            })} placeholder={c.yourLinkPlaceholder} aria-describedby="intro-link-help" className={field} autoComplete="url" data-id="element-4123" data-mp="2gwyppymus74u" />
                <p id="intro-link-help" className="mt-2 text-xs leading-relaxed text-white/40" data-id="element-4124" data-mp="2viclg0nntck4">
                  {c.yourLinkHelp}
                </p>
              </div>
              <div data-id="element-4125" data-mp="2lmst1o8b0w04">
                <label className={label} htmlFor="intro-message" data-id="element-4126" data-mp="2ln2i99liyz4r">
                  {c.message} <span style={{
                color: GOLD
              }} data-id="element-4127" data-mp="27vny2zfvsrw4">*</span>
                </label>
                <textarea id="intro-message" rows={4} value={form.message} onChange={e => setForm({
              ...form,
              message: e.target.value
            })} placeholder={c.messagePlaceholder} className={`${field} resize-none`} data-id="element-4128" data-mp="2bw7v830gfvi9" />
              </div>
            </div>

            <div className="mt-6 flex items-start gap-2.5 text-xs leading-relaxed text-white/45" data-id="element-4129" data-mp="3172u2vgqbw65">
              <ShieldCheckIcon className="w-4 h-4 flex-shrink-0 mt-px" style={{
            color: GOLD
          }} data-id="element-4130" data-mp="2we9n00xsgbqw" />
              <span data-id="element-4131" data-mp="2mhn4vjwujysk">{INTRO_SHEET_URL ? c.privacy : c.fallbackNote}</span>
            </div>

            <div className="mt-7 flex flex-col-reverse sm:flex-row sm:justify-end gap-3" data-id="element-4132" data-mp="2mfaganwl2q6j">
              <button onClick={onClose} className="px-6 py-3 rounded-full text-sm font-bold text-white/70 border border-white/20 transition-colors hover:bg-white/5 hover:text-white" data-id="element-4133" data-mp="2hkwp6xoyf57o">
                {c.cancel}
              </button>
              <button onClick={submit} disabled={!isValid || status === 'sending'} className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full text-sm font-bold text-[#0b1f3f] transition-transform duration-150 enabled:hover:scale-[1.02] disabled:opacity-40 disabled:cursor-not-allowed" style={{
            backgroundColor: GOLD
          }} data-id="element-4134" data-mp="bou19kdffd84">
                {status === 'sending' ? <>
                    <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-4135" data-mp="syefd2dfxp54" />
                    {c.sending}
                  </> : <>
                    <SendIcon className="w-4 h-4" data-id="element-4136" data-mp="9qpethqybl8" />
                    {c.send}
                  </>}
              </button>
            </div>
          </div>}
      </motion.div>
    </div>;
}
function GuestCard({
  guest,
  c,
  onRequest
}: {
  guest: NetworkingGuest;
  c: (typeof COPY)['EN'];
  onRequest: () => void;
}) {
  return <article className="group flex flex-col h-full rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden transition-colors duration-200 hover:border-[#c9a227]/40 hover:bg-white/[0.05]" data-id="element-4137" data-mp="1gql5vkwhj1wm">
      {/* Portrait fills the top of the card so faces read at a glance */}
      <div className="relative w-full aspect-square bg-white/[0.06] overflow-hidden" data-id="element-4138" data-mp="3cr538xpjg6jg">
        {guest.photo ? <img src={guest.photo} alt={guest.name} loading="lazy" decoding="async" width={640} height={640} className="absolute inset-0 w-full h-full object-cover object-[center_22%]" data-id="element-4139" data-mp="3qqmvxo5yg3y3" /> : <div className="absolute inset-0 flex items-center justify-center" data-id="element-4140" data-mp="2u305c6vkbfsk">
            <span className="font-bebas text-5xl tracking-wider text-white/25" data-id="element-4141" data-mp="37tbzscfmdqmv">
              {initialsOf(guest.name)}
            </span>
          </div>}
        <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.15em] text-[#0b1f3f]" style={{
        backgroundColor: GOLD
      }} data-id="element-4142" data-mp="2fbznxccu8bka">
          {guest.sector}
        </span>
        {guest.boardOrder !== undefined && <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.15em] text-white bg-[#0b1f3f]/85 ring-1 ring-white/20" data-id="element-4143" data-mp="ulu8qu8s5yn9">
            {c.boardBadge}
          </span>}
      </div>

      <div className="p-6 flex-1 flex flex-col" data-id="element-4144" data-mp="luli65syvnzc">
        <h3 className="font-bebas text-3xl tracking-wider text-white leading-none" data-id="element-4145" data-mp="lr60cv7ws0u8">
          {guest.name}
        </h3>
        {guest.title && <p className="mt-2 text-sm font-semibold text-white/80 leading-snug" data-id="element-4146" data-mp="1djopxvk2bggq">
            {guest.title}
          </p>}
        {guest.organization && <p className="text-sm text-white/50 leading-snug" data-id="element-4147" data-mp="14jru99z99hc6">
            {guest.organization}
          </p>}
        {guest.location && <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-white/45" data-id="element-4148" data-mp="3kaeaakaknt5c">
            <MapPinIcon className="w-3.5 h-3.5" data-id="element-4149" data-mp="3pbadswr0hda8" />
            {guest.location}
          </p>}

        {guest.bio && <p className="mt-4 text-sm leading-relaxed text-white/65" data-id="element-4150" data-mp="ktr437k6golg">
            {guest.bio}
          </p>}

        {guest.lookingFor && <div className="mt-5 rounded-xl border border-[#c9a227]/20 bg-[#c9a227]/[0.07] px-4 py-3" data-id="element-4151" data-mp="atqgh9f9zmbg">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] mb-1.5" style={{
          color: GOLD
        }} data-id="element-4152" data-mp="5vypchmgg48b">
              {c.lookingFor}
            </p>
            <p className="text-sm leading-relaxed text-white/75" data-id="element-4153" data-mp="yjb2602tcxof">
              {guest.lookingFor}
            </p>
          </div>}

        <div className="mt-auto pt-6" data-id="element-4154" data-mp="trjkkf1my2af">
          <div className="flex flex-wrap items-center gap-2" data-id="element-4155" data-mp="ou17qdkhz2x5">
            {guest.linkedin && <a href={guest.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-bold text-white/75 border border-white/15 transition-colors hover:border-white/35 hover:text-white" data-id="element-4156" data-mp="1m9f4830cspdb">
                <LinkedinIcon className="w-3.5 h-3.5" data-id="element-4157" data-mp="1ttppg2xp9el5" />
                {c.linkedin}
              </a>}
            {guest.website && <a href={guest.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-bold text-white/75 border border-white/15 transition-colors hover:border-white/35 hover:text-white" data-id="element-4158" data-mp="23r4540bstzce">
                <GlobeIcon className="w-3.5 h-3.5" data-id="element-4159" data-mp="2hjk2ollxl55x" />
                {c.website}
              </a>}
            {guest.feature && <a href={guest.feature} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-bold text-white/75 border border-white/15 transition-colors hover:border-white/35 hover:text-white" data-id="element-4160" data-mp="2mjg5wimt0skf">
                <NewspaperIcon className="w-3.5 h-3.5" data-id="element-4161" data-mp="3rb0pjpro2gv" />
                {c.feature}
              </a>}
          </div>
          {guest.acceptsIntros !== false && <button onClick={onRequest} className="mt-3 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-bold text-[#0b1f3f] transition-transform duration-150 hover:scale-[1.01]" style={{
          backgroundColor: GOLD
        }} data-id="element-4162" data-mp="dq0k5gxb9uz8">
              <SendIcon className="w-4 h-4" data-id="element-4163" data-mp="rh5cdxycnkzj" />
              {c.requestIntro}
            </button>}
        </div>
      </div>
    </article>;
}
export function VoicesOfImpactNetworkingPage() {
  const {
    language,
    setLanguage
  } = useLanguage();
  const isEs = language === 'ES';
  const c = isEs ? COPY.ES : COPY.EN;
  const [query, setQuery] = useState('');
  const [sector, setSector] = useState<string>('');
  const [activeGuest, setActiveGuest] = useState<NetworkingGuest | null>(null);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const sectors = useMemo(() => Array.from(new Set(NETWORKING_GUESTS.map(g => g.sector))).sort((a, b) => a.localeCompare(b)), []);
  const guests = useMemo(() => {
    const q = query.trim().toLowerCase();
    return NETWORKING_GUESTS.filter(g => {
      if (sector && g.sector !== sector) return false;
      if (!q) return true;
      return [g.name, g.title, g.organization, g.location, g.bio].filter(Boolean).some(field => (field as string).toLowerCase().includes(q));
    }).sort((a, b) => {
      // HAA board members lead the directory, in board order.
      if (a.boardOrder || b.boardOrder) {
        return (a.boardOrder ?? Infinity) - (b.boardOrder ?? Infinity);
      }
      // Featured guests of the evening come next.
      if (a.featuredOrder || b.featuredOrder) {
        return (a.featuredOrder ?? Infinity) - (b.featuredOrder ?? Infinity);
      }
      // Everyone else is grouped by industry, then alphabetically.
      if (a.sector !== b.sector) return a.sector.localeCompare(b.sector);
      return a.name.localeCompare(b.name);
    });
  }, [query, sector]);
  const chip = (active: boolean) => `px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.12em] border transition-colors duration-150 ${active ? 'text-[#0b1f3f] border-transparent' : 'text-white/60 border-white/15 hover:border-white/35 hover:text-white'}`;
  return <div className="min-h-screen w-full bg-[#0b1f3f] text-white font-nunito" data-id="element-4164" data-mp="t1b92fscbixq">
      <header className="relative z-10 w-full px-6 py-6 sm:px-12 flex items-center justify-between gap-4 border-b border-[#c9a227]/30" data-id="element-4165" data-mp="t3e2zawm5w7q">
        <a href="#/voices-of-impact-ny" className="inline-flex items-center gap-2 text-sm font-semibold text-white/70 transition-colors hover:text-white" data-id="element-4166" data-mp="2nesxs64heluc">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-4167" data-mp="32hkkr2gvfzco" />
          {c.back}
        </a>
        <button onClick={() => setLanguage(isEs ? 'EN' : 'ES')} className="px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.14em] border transition-colors duration-150 hover:bg-[#c9a227] hover:text-[#0b1f3f]" style={{
        borderColor: `${GOLD}66`,
        color: GOLD
      }} data-id="element-4168" data-mp="2xizkx45j7hor">
          {isEs ? 'English' : 'Español'}
        </button>
      </header>

      <main data-id="element-4169" data-mp="3laofkr5tsi8v">
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 pt-14 pb-10" data-id="element-4170" data-mp="3g9hbp36t037h">
          <p className="text-xs font-bold uppercase tracking-[0.25em] mb-4" style={{
          color: GOLD
        }} data-id="element-4171" data-mp="3gblbpa1l0i2h">
            {c.eyebrow}
          </p>
          <h1 className="font-bebas text-5xl sm:text-6xl lg:text-7xl tracking-wider leading-none mb-6" data-id="element-4172" data-mp="3q86io0x5w38y">
            {c.title}
          </h1>
          <p className="text-base sm:text-lg leading-relaxed text-white/70 max-w-3xl" data-id="element-4173" data-mp="1vtngjzgj81rg">
            {c.intro}
          </p>
          <div className="mt-6 inline-flex items-start gap-2.5 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 max-w-2xl" data-id="element-4174" data-mp="1xbceoyhoaspq">
            <ShieldCheckIcon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{
            color: GOLD
          }} data-id="element-4175" data-mp="228cj74yvjneq" />
            <p className="text-sm leading-relaxed text-white/60" data-id="element-4176" data-mp="2balqzn2rd2of">{c.privacy}</p>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 pb-6" data-id="element-4177" data-mp="1ekl7a2h8xcdz">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4" data-id="element-4178" data-mp="1eii56k2tjssw">
            <div className="relative flex-1 max-w-md" data-id="element-4179" data-mp="1im6f39exb0hb">
              <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35" data-id="element-4180" data-mp="1nkhrnrwlrp38" />
              <input type="search" value={query} onChange={e => setQuery(e.target.value)} aria-label={c.searchLabel} placeholder={c.searchPlaceholder} className="w-full rounded-full bg-white/5 border border-white/15 pl-11 pr-4 py-3 text-sm text-white placeholder-white/35 outline-none transition-colors focus:border-[#c9a227] focus:bg-white/10" data-id="element-4181" data-mp="1sbzp45zf37jg" />
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-4182" data-mp="idqaaq29esb8">
              <button onClick={() => setSector('')} className={chip(sector === '')} style={sector === '' ? {
              backgroundColor: GOLD
            } : undefined} data-id="element-4183" data-mp="ic5bupctqp83">
                {c.all}
              </button>
              {sectors.map(s => <button key={s} onClick={() => setSector(s)} className={chip(sector === s)} style={sector === s ? {
              backgroundColor: GOLD
            } : undefined} data-id="element-4184" data-mp="4ljuj530qncb">
                  {s}
                </button>)}
            </div>
          </div>
          <p className="mt-4 text-xs font-bold uppercase tracking-[0.18em] text-white/40" data-id="element-4185" data-mp="3mtrc0aglt1eh">
            {guests.length} {guests.length === 1 ? c.countOne : c.countMany}
          </p>
        </section>

        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 pb-20" data-id="element-4186" data-mp="387lutqfwzemn">
          {guests.length === 0 ? <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-8 py-16 text-center" data-id="element-4187" data-mp="2y7bi06xsk980">
              <SparklesIcon className="w-8 h-8 mx-auto mb-4" style={{
            color: `${GOLD}80`
          }} data-id="element-4188" data-mp="335wo2mzq5n4d" />
              <p className="text-base text-white/65 mb-5" data-id="element-4189" data-mp="3qw91gddqoc2c">{c.empty}</p>
              <button onClick={() => {
            setQuery('');
            setSector('');
          }} className="px-6 py-2.5 rounded-full text-sm font-bold text-white/75 border border-white/20 transition-colors hover:border-white/40 hover:text-white" data-id="element-4190" data-mp="3vv3h56getki8">
                {c.clear}
              </button>
            </div> : <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 items-stretch" data-id="element-4191" data-mp="ig76i27mzn7z">
              {guests.map(guest => <GuestCard key={guest.id} guest={guest} c={c} onRequest={() => setActiveGuest(guest)} data-id="element-4192" data-mp="234i1x9c5se2i" />)}
            </div>}
        </section>
      </main>

      <footer className="border-t border-white/10 px-6 py-8 text-center" data-id="element-4193" data-mp="27y424214yphf">
        <p className="text-xs uppercase tracking-[0.2em] text-white/40" data-id="element-4194" data-mp="1k774lua37upp">
          {c.footer}
        </p>
      </footer>

      <AnimatePresence data-id="element-4195" data-mp="1f9faqupvtyiv">
        {activeGuest && <IntroRequestDialog guest={activeGuest} c={c} onClose={() => setActiveGuest(null)} data-id="element-4196" data-mp="1faqewvn8p8ai" />}
      </AnimatePresence>
    </div>;
}